// Format badges: the twelve format icons (formats.ts) in three treatments.
//   <FormatIcon id="documentary" />                 the icon alone (currentColor, or the gel inside .cao-fmt)
//   <FormatBadge id="cartoon" />                    chip: icon + short name in a gel-washed pill
//   <FormatBadge id="short-film" variant="tile" selected />   the type picker / showcase card
// A format takes its group's gel (tokens.css --cao-gel-*), so the picker, chips and landing read as one family.
import type { CSSProperties } from "react";
import { FORMAT_BRAND, FORMAT_ICONS, type FormatId } from "./formats";
import "./brand.css";

export { FORMAT_BRAND, FORMAT_GROUPS, FORMAT_ORDER, GELS, type FormatGroup, type FormatId } from "./formats";

export interface FormatIconProps {
  id: FormatId;
  size?: number | string;
  /** Stroke width at 24px; scales with size. */
  stroke?: number;
  /** Colour; defaults to currentColor (inside a badge, the group's gel). */
  color?: string;
  /** Give it an accessible name; without one the icon is decorative. */
  title?: string;
  className?: string;
  style?: CSSProperties;
}

export function FormatIcon({ id, size = 24, stroke = 1.75, color, title, className = "", style }: FormatIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke={color ?? "currentColor"}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`cao-fmt-icon ${className}`}
      style={style}
      {...(title ? { role: "img", "aria-label": title } : { "aria-hidden": true, focusable: "false" })}
    >
      {FORMAT_ICONS[id].map(([tag, a], i) => {
        const Tag = tag;
        return <Tag key={i} {...(a as Record<string, string | number>)} />;
      })}
    </svg>
  );
}

export interface FormatBadgeProps {
  id: FormatId;
  /** icon: the gel-coloured icon alone. chip: icon + short name in a gel-washed pill. tile: the picker card. */
  variant?: "icon" | "chip" | "tile";
  /** Override the label (defaults: chip = short name, tile = full name). */
  label?: string;
  /** Tile only: the selected tile gets the hand-frame corners in its gel. */
  selected?: boolean;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

export function FormatBadge({ id, variant = "chip", label, selected, size, className = "", style }: FormatBadgeProps) {
  const f = FORMAT_BRAND[id];
  const g = f.group.toLowerCase();
  if (variant === "icon") {
    return (
      <span className={`cao-fmt ${className}`} data-group={g} style={style}>
        <FormatIcon id={id} size={size ?? 24} title={label ?? f.name} />
      </span>
    );
  }
  if (variant === "tile") {
    return (
      <span className={`cao-fmt cao-fmt-tile ${selected ? "cao-frame is-selected" : ""} ${className}`} data-group={g} style={style}>
        <FormatIcon id={id} size={size ?? 30} />
        <span>
          <span className="cao-fmt-tile-name">{label ?? f.name}</span>
          <span className="cao-fmt-tile-group">{f.group}</span>
        </span>
      </span>
    );
  }
  return (
    <span className={`cao-fmt cao-fmt-chip ${className}`} data-group={g} style={style}>
      <FormatIcon id={id} size={size ?? 16} stroke={2} />
      {label ?? f.short}
    </span>
  );
}
